// Capa de datos · Control de Asistencia / Vista en sala (kiosko).
//
// FRONTERA CON EL BACKEND DEL GUARDIAN
// El backend de sala (verificación de puerta 2, scope solo-sala, lockout, lista
// de técnicos autorizados, clave de Visitante) lo construye el GUARDIÁN. Aquí el
// front queda preparado CONTRA EL CONTRATO, sin inventar datos:
//   · Si un endpoint de AUTH aún no existe → NO se simula un login correcto: se
//     devuelve { pendiente:true } y la UI muestra un estado honesto.
//   · La lista de técnicos NO se hardcodea: sale del endpoint del Guardián.
//
// Contrato (lo entrega el Guardián):
//   POST /asistencia/control/sala/abrir           { usuario, password } → sesión de sala
//   GET  /asistencia/control/sala/tecnicos?sede=  → { tecnicos:[{id,nombre}] }  (SIN PII)
//   POST /asistencia/control/sala/verificar       { sede, tecnico_id|"visitante", password } → sesión
//   GET  /asistencia/control/dia?fecha=&sede=     → listado de asistencia (reusa Control de Asistencia)
//   POST /asistencia/control/marcar               { fecha, hora, cliente_id, asistio }
//   POST /asistencia/control/walk-in              { fecha, hora, cliente_id }
//   GET  /asistencia/control/buscar?q=            (proyección SIN PII: id + nombre)
//
// MAQUETA: para revisar el ESTILO sin backend existe un modo maqueta EXPLÍCITO
// (solo si se pide con maqueta=true · p.ej. ?maqueta=1). Va SIEMPRE con banner
// visible y NUNCA se activa en el flujo real ni falsea la verificación de auth.

import axios from "axios";
import { API } from "../api";

const BASE = `${API}/asistencia/control`;
const SALA = `${BASE}/sala`;
const CHAT = `${API}/chat-clinica`;

// ── Chat clínico fisio ↔ sala (lado 'sala') · backend #846 ───────────────────
// El bocadillo de la vista en sala lee los NO leídos reales por paciente.
// Degrada con elegancia: si no hay permiso/aún sin publicar → vacío, sin romper.

/** {paciente_id: nº no leídos} de los mensajes que escribió el fisio y la sala no ha leído. */
export async function chatNoLeidosResumenSala() {
  try {
    const r = await axios.get(`${CHAT}/no-leidos-resumen`, { params: { lado: "sala" } });
    return r.data?.por_paciente || {};
  } catch { return {}; }
}

/** Mensajes reales de un paciente (conversación mezclada fisio↔sala). */
export async function chatMensajes(paciente_id) {
  try {
    const r = await axios.get(`${CHAT}/${paciente_id}/mensajes`);
    return Array.isArray(r.data) ? r.data : [];
  } catch { return []; }
}

/** Marca como leídos (lado sala) los mensajes del fisio para este paciente. */
export async function chatMarcarLeidoSala(paciente_id) {
  try { await axios.post(`${CHAT}/${paciente_id}/marcar-leido`, null, { params: { lado: "sala" } }); }
  catch { /* sin permiso o pendiente → no-op */ }
}

// status que indican "endpoint aún no publicado por el Guardián"
const _pendiente = (e) => {
  const s = e?.response?.status;
  return s === 404 || s === 501 || s === undefined || (typeof s === "number" && s >= 500);
};

// ─────────────────────────── Puerta 1 · abrir sala ───────────────────────────
export async function abrirSala({ usuario, password }) {
  try {
    const r = await axios.post(`${SALA}/abrir`, { usuario, password });
    return { ok: true, ...r.data };
  } catch (e) {
    if (e?.response?.status === 401) return { ok: false, error: "Usuario o contraseña de sala incorrectos." };
    if (_pendiente(e)) return { ok: false, pendiente: true };
    throw e;
  }
}

// ───────────────────── Puerta 2 · técnicos autorizados (SIN PII) ─────────────────────
export async function listarTecnicosAutorizados({ sede }) {
  try {
    const r = await axios.get(`${SALA}/tecnicos`, { params: { sede } });
    return { tecnicos: r.data.tecnicos || [] };
  } catch (e) {
    if (_pendiente(e)) return { tecnicos: [], pendiente: true };
    throw e;
  }
}

// ───────────────────── Puerta 2 · verificar (técnico o Visitante) ─────────────────────
// tecnico_id = id real del técnico, o la cadena "visitante" para el sustituto.
export async function verificarTecnico({ sede, tecnico_id, password }) {
  try {
    const r = await axios.post(`${SALA}/verificar`, { sede, tecnico_id, password });
    return { ok: true, ...r.data };
  } catch (e) {
    if (e?.response?.status === 401) return { ok: false, error: "Contraseña incorrecta." };
    if (e?.response?.status === 423) return { ok: false, error: "Demasiados intentos. Inténtalo más tarde." };
    if (_pendiente(e)) return { ok: false, pendiente: true };
    throw e;
  }
}

// ─────────────────────────── Asistencia · pasar lista ───────────────────────────
export async function cargarDia({ fecha, sede, maqueta = false }) {
  if (maqueta) return { ...MAQUETA, maqueta: true };
  try {
    const r = await axios.get(`${BASE}/dia`, { params: { fecha, sede } });
    return { ...r.data };
  } catch (e) {
    // 403 = permiso aún no concedido (deny-by-default · lo activa el Guardián) → pendiente.
    if (_pendiente(e) || e?.response?.status === 403) {
      return { dias: [], horas: [], slots: {}, pendiente: true };
    }
    throw e;
  }
}

export async function marcarAsistencia({ fecha, hora, cliente_id, asistio, sede }) {
  // sede SIEMPRE: el backend la exige (admin sin sede_fija la necesita); con cuenta
  // de sala el servidor la fuerza igualmente desde sede_fija (anti-IDOR).
  return axios.post(`${BASE}/marcar`, { fecha, hora, cliente_id, asistio, sede });
}

// Registra una CLASE ADICIONAL (sin reserva) en el usuario: el endpoint /walk-in
// la anota como asistencia extra (es_extra) sobre la ficha del cliente.
export async function registrarWalkIn({ fecha, hora, cliente_id, sede }) {
  return axios.post(`${BASE}/walk-in`, { fecha, hora, cliente_id, sede });
}

// Cliente DE BAJA que intenta entrar: avisa a recepción (notificación grave). No entra.
export async function advertirBaja({ cliente_id, fecha, hora, sede }) {
  return axios.post(`${BASE}/advertir-baja`, { cliente_id, fecha, hora, sede });
}

export async function buscarWalkIn(q, { maqueta = false } = {}) {
  if (maqueta) {
    return MAQUETA_CLIENTES.filter((n) => n.nombre.toLowerCase().includes(q.toLowerCase())).slice(0, 10);
  }
  try {
    const r = await axios.get(`${BASE}/buscar`, { params: { q } });
    return r.data;
  } catch (e) {
    if (_pendiente(e)) return [];
    throw e;
  }
}

// ───────────────────── MAQUETA (solo revisión de estilo · explícita) ─────────────────────
const MAQUETA_CLIENTES = [
  { id: "m1", nombre: "Esther Camps Vidal" },
  { id: "m2", nombre: "Estela Ruano Gil" },
  { id: "m3", nombre: "Esteban Mora Ferrer" },
  { id: "m4", nombre: "Nuria Camps Soler" },
  { id: "m5", nombre: "Marc Esteve Roca" },
  { id: "m6", nombre: "Elena Estévez Pou", es_baja: true },
];

const MAQUETA = {
  dias: [
    { fecha: "2026-06-22", label: "Lunes 22 de junio" },
    { fecha: "2026-06-23", label: "Martes 23 de junio" },
    { fecha: "2026-06-24", label: "Miércoles 24 de junio" },
    { fecha: "2026-06-25", label: "Jueves 25 de junio" },
    { fecha: "2026-06-26", label: "Viernes 26 de junio" },
  ],
  horas: ["08:00", "09:00", "10:00", "11:00", "12:00", "14:00", "17:00", "18:00", "19:00", "20:00"],
  horas_suave: ["17:00"],   // demo: franja suave (pill menta)
  slots: {
    "10:00": [
      { id: "1", nombre: "Ana Pérez García", asistio: true, paciente_fisio: true, chat: true, tiene_ficha_mpd: true,
        salud: { autor: "Thais", nota: "Riesgo de mareos: intentar no poner la cabeza boca abajo en los dos primeros meses." },
        ficha_mpd: { advertencia_corta: "Riesgo de mareos.", adaptaciones_mpd: "Evitar inversiones y cambios bruscos de posicion.",
          anotaciones: [{ autor: "Thais", fecha: "Lun 22 jun", texto: "Vigilar tolerancia las primeras semanas." }] },
        chat_msgs: [{ autor: "Thais", rol: "fisio", texto: "Ana con riesgo de mareos, nada boca abajo los 2 primeros meses.", ts: "Mar 09:05" }] },
      { id: "2", nombre: "Luis Martín Soler", asistio: false, deuda: true },
      { id: "3", nombre: "Marta Ruiz Díaz", asistio: true, es_monitorizado: true, paciente_fisio: true },
      { id: "4", nombre: "Pau Gomis Llopis", asistio: false, cambio: true, habitual: "Martes 18h · Jueves 11h" },
      { id: "5", nombre: "Sara Vidal Roig", asistio: false, cambio: true, habitual: "Lunes 9h · Miércoles 9h", suave: true },
      { id: "6", nombre: "Jordi Camps Mas", asistio: false,
        aviso: { nota: "Pendiente de actualizar datos de contacto y firmar el consentimiento." } },
      { id: "7", nombre: "Núria Soler Pou", asistio: true },
      { id: "8", nombre: "Marc Esteve Roca", asistio: false },
      { id: "9", nombre: "Elena Ferrer Gil", asistio: false, deuda: true },
      { id: "10", nombre: "Pol Ribas Nadal", asistio: true },
    ],
    default: [
      { id: "x1", nombre: "Carla Sender Vidal", asistio: false },
      { id: "x2", nombre: "Jordi Bonet Mas", asistio: false },
    ],
  },
};
